import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useThemeMode } from '../../theme/ThemeContext';
import type { BreadcrumbItem } from '../shared/types';
import ProfileMenu from './ProfileMenu';

export function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const items: BreadcrumbItem[] = [];
  const rootSegment = segments[0];
  const rootLabel = capitalize(rootSegment);

  if (segments.length === 1) {
    items.push({ label: rootLabel });
  } else if (segments.length === 2) {
    items.push({ label: rootLabel, path: `/${rootSegment}` });
    const id = segments[1];
    const singularLabel = getSingularLabel(rootSegment);
    items.push({ label: `${singularLabel} #${id}` });
  } else if (segments.length >= 3) {
    items.push({ label: rootLabel, path: `/${rootSegment}` });
    const action = segments[segments.length - 1];
    const singularLabel = getSingularLabel(rootSegment);
    items.push({ label: `${capitalize(action)} ${singularLabel}` });
  }
  return items;
}

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getSingularLabel(segment: string): string {
  const map: Record<string, string> = {
    machines: 'Machine',
    templates: 'Template',
    schedules: 'Schedule',
    results: 'Result',
    alerts: 'Alert',
    settings: 'Settings',
  };
  return map[segment] || capitalize(segment);
}

export interface TopBarProps {
  breadcrumbs?: BreadcrumbItem[];
  onMobileMenuToggle?: () => void;
  collapsed?: boolean;
  onToggle?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMobileMenuToggle, collapsed, onToggle }) => {
  const { mode, toggleMode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      {/* Logo & hamburger toggle */}
      <Box sx={{ width: isMobile ? 'auto' : 228, display: 'flex', alignItems: 'center' }}>
        {!isMobile && (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="TrueOne"
              sx={{ height: 32, cursor: 'pointer' }}
            />
          </Box>
        )}
        <Avatar
          variant="rounded"
          sx={{
            width: 34,
            height: 34,
            fontSize: '1.2rem',
            cursor: 'pointer',
            overflow: 'hidden',
            transition: 'all .2s ease-in-out',
            color: theme.palette.secondary.dark,
            bgcolor: theme.palette.secondary.light,
            '&:hover': {
              color: theme.palette.secondary.light,
              bgcolor: theme.palette.secondary.dark,
            },
          }}
          onClick={isMobile ? onMobileMenuToggle : onToggle}
        >
          <MenuIcon fontSize="small" />
        </Avatar>
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Right side actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Dark/Light mode toggle */}
        <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'}>
          <Avatar
            variant="rounded"
            sx={{
              width: 34,
              height: 34,
              fontSize: '1.2rem',
              cursor: 'pointer',
              transition: 'all .2s ease-in-out',
              color: theme.palette.primary.dark,
              bgcolor: theme.palette.primary.light,
              '&:hover': {
                color: theme.palette.primary.light,
                bgcolor: theme.palette.primary.dark,
              },
            }}
            onClick={toggleMode}
          >
            {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </Avatar>
        </Tooltip>

        {/* Notification icon */}
        <Tooltip title="Notifications">
          <Avatar
            variant="rounded"
            sx={{
              width: 34,
              height: 34,
              fontSize: '1.2rem',
              cursor: 'pointer',
              transition: 'all .2s ease-in-out',
              color: theme.palette.primary.dark,
              bgcolor: theme.palette.primary.light,
              '&:hover': {
                color: theme.palette.primary.light,
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            <NotificationsNoneIcon fontSize="small" />
          </Avatar>
        </Tooltip>

        {/* Profile menu */}
        <ProfileMenu />
      </Box>
    </>
  );
};
